import * as React from 'react';

import { withRouter } from 'app/context/Router';

import {
  ComposableTableWithFixedPageHeader,
  getButtonCellsGenerator,
  getDatestampCellGenerator,
  getTextCellGenerator,
  getUserAvatarCellGenerator,
  // @ts-ignore
} from 'components/ComposableTable';

import Link from 'components/Link';
import Row from 'components/Row';
// @ts-ignore
import ScrollLoadZone from 'components/ScrollLoadZone';
import Text from 'components/Text';

// @ts-ignore
import { ColumnHeader } from 'components/ComposableTable';
import { ReachabilityInfo } from 'components/ReachabilityBadge';
import { RouterState } from 'lib/ReactRouterTypes';

type UserInfo = {
  id: number;
  uuid: string;
  color: string | null;
  firstName: string | null;
  lastName: string | null;
  initials: string | null;
  profilePictureUrl: string | null;
  reachabilityInfo: ReachabilityInfo;
};

export type GroupMembershipRequest = {
  uuid: string;
  user: UserInfo;
  requestMessage: string;
  createdAt: string;
};

type Props = {
  fetchMore: () => Promise<any>;
  hasNextPage: boolean;
  loading: boolean;
  groupMembershipRequests: GroupMembershipRequest[];
  onApproval: (uuid: string) => void;
  onDenial: (uuid: string) => void;
  router: RouterState;
  pageHeader: React.ReactElement<any>;
  pageHeaderHeight: number;
};

class PendingMembershipsTable extends React.Component<Props> {
  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap: { [name: string]: ColumnHeader } = {
      avatar: {
        name: '',
        type: 'avatar',
      },
      actions: {
        name: 'Actions',
        icon: {
          iconName: 'question-circle',
          tooltip:
            "Accepting will add them to your class, denying will notify them that they're not in the class.",
        },
      },
      name: { name: 'Name' },
      requestMessage: { name: 'Message' },
      requestSent: { name: 'Request sent' },
    };

    return columnMap[columnName];
  };

  columnHeaderNames = ['avatar', 'name', 'requestMessage', 'requestSent', 'actions'];

  columnHeaders = (): Array<ColumnHeader> => {
    return this.columnHeaderNames.map<ColumnHeader>((name: string) =>
      this.columnHeaderForColumn(name)
    );
  };

  columnGenerators = (): Array<Function> => {
    const { onApproval, onDenial } = this.props;

    const components: { [name: string]: (data: GroupMembershipRequest) => React.ReactElement } = {
      avatar: getUserAvatarCellGenerator({ userField: 'user' }),
      actions: getButtonCellsGenerator([
        {
          fieldForAction: 'uuid',
          buttonLabel: 'Approve',
          onClickAction: onApproval,
          buttonProps: {
            level: 'primary',
            className: 'PendingMemberships-approveButton',
          },
        },
        {
          fieldForAction: 'uuid',
          buttonLabel: 'Deny',
          onClickAction: onDenial,
          buttonProps: {
            level: 'secondary',
            className: 'PendingMemberships-denyButton',
          },
        },
      ]),
      name: getTextCellGenerator(
        (row: GroupMembershipRequest) => [row.user.firstName, row.user.lastName].join(' '),
        { 'data-test-selector': 'name' }
      ),
      requestMessage: getTextCellGenerator('requestMessage', {
        'data-test-selector': 'request_message',
      }),
      requestSent: getDatestampCellGenerator('createdAt', { 'data-test-selector': 'request_sent' }),
    };

    return this.columnHeaderNames.map<Function>((name: string) => components[name]);
  };

  renderNoResultsView = () => {
    const { loading, router } = this.props;

    if (loading) {
      return null;
    }

    return (
      <div data-test="no-results-view">
        <Text align="center" size="medium">
          There are currently no requests to join your class.
        </Text>
      </div>
    );
  };

  render() {
    const {
      fetchMore,
      hasNextPage,
      loading,
      groupMembershipRequests,
      pageHeader,
      pageHeaderHeight,
    } = this.props;

    return (
      <div className="PendingMemberships-table">
        <Row size="large">
          <ComposableTableWithFixedPageHeader
            columnGenerators={this.columnGenerators()}
            columnHeaders={this.columnHeaders()}
            fixedHeader
            keyField={(row: GroupMembershipRequest) => row.user.id}
            loading={loading}
            rows={groupMembershipRequests}
            className="PendingMembershipsTable"
            pageHeaderHeight={pageHeaderHeight}
            pageHeader={pageHeader}
          />
        </Row>
        {!loading && hasNextPage && <ScrollLoadZone threshold={500} onZoneActivated={fetchMore} />}
        {groupMembershipRequests &&
          groupMembershipRequests.length === 0 &&
          this.renderNoResultsView()}
      </div>
    );
  }
}

export default withRouter(PendingMembershipsTable);
